import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { DashboardSkeleton, ErrorState, Avatar } from "@/components/common";
import CotaPendenteBanner from "@/components/CotaPendenteBanner";
import CotaRealCard from "@/components/CotaRealCard";
import FaturamentoSankhyaCards from "@/components/FaturamentoSankhyaCards";
import Dica from "@/components/Dica";
import ResumoDoDia from "@/components/ResumoDoDia";
import MetaBatida from "@/components/MetaBatida";
import RegistrarAtividade from "@/components/RegistrarAtividade";
import HelpTip from "@/components/HelpTip";
import { DICAS } from "@/lib/dicas";
import { brl, pct, num, roleLabel, fmtDate } from "@/lib/format";
import { corEtapa } from "@/lib/stageColors";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  Target, Users, KanbanSquare, CalendarClock, CheckCircle2, X, Bell, TrendingUp, ClipboardList,
  ChevronRight, ArrowLeft, Home, UserPlus, PauseCircle, Trophy, XCircle, Percent, Info, Plus,
} from "@/components/icons";

function ReminderBanner({ ownerId }) {
  const [agenda, setAgenda] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("mpires_reminder_dismiss") === new Date().toDateString());
  const navigate = useNavigate();
  useEffect(() => {
    if (ownerId) { setAgenda(null); return; }
    api.get("/agenda").then((r) => setAgenda(r.data)).catch(() => {});
  }, [ownerId]);
  if (dismissed || !agenda) return null;
  const atrasadas = agenda.counts?.atrasadas || 0;
  const hoje = agenda.counts?.hoje || 0;
  if (atrasadas + hoje === 0) return null;
  const close = () => { localStorage.setItem("mpires_reminder_dismiss", new Date().toDateString()); setDismissed(true); };
  // LOTE UX-1 B2 — no celular, faixa compacta de uma linha "N atrasadas · Ver agenda".
  // Desktop mantém o banner atual.
  const resumo = `${atrasadas > 0 ? (atrasadas === 1 ? "1 atrasada" : `${atrasadas} atrasadas`) : ""}${atrasadas > 0 && hoje > 0 ? " · " : ""}${hoje > 0 ? `${hoje} para hoje` : ""}`;
  return (
    <>
      <button
        onClick={() => navigate("/atividades")}
        data-testid="reminder-banner-mobile"
        className="md:hidden mb-4 w-full rounded-lg bg-navy text-white px-3 py-2 flex items-center justify-between gap-2 text-xs font-medium"
      >
        <span className="flex items-center gap-2 min-w-0 truncate">
          <Bell className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{resumo || "Atividades pendentes"}</span>
        </span>
        <span className="font-semibold text-brand-soft shrink-0 flex items-center gap-1">Ver agenda <ChevronRight className="w-3.5 h-3.5" /></span>
      </button>
      <div className="hidden md:flex mb-6 rounded-xl bg-navy text-white p-4 items-center gap-4 relative overflow-hidden" data-testid="reminder-banner">
        <div className="absolute right-0 top-0 w-24 h-24 rounded-full bg-brand/30 blur-2xl" />
        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Bell className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0 relative">
          <div className="font-semibold text-sm">Você tem atividades pendentes</div>
          <div className="text-xs text-white/70">{resumo}</div>
        </div>
        <button onClick={() => navigate("/atividades")} className="text-xs font-semibold bg-brand hover:bg-brand-hover px-3 py-2 rounded-lg transition-colors relative" data-testid="reminder-open">Ver agenda</button>
        <button onClick={close} className="p-1.5 rounded-lg hover:bg-white/10 relative" data-testid="reminder-dismiss"><X className="w-4 h-4" /></button>
      </div>
    </>
  );
}

function useDrillChildren(tree, ownerId) {
  return useMemo(() => {
    if (!tree) return { level: "empresa", subject: null, children: [], parents: [] };
    const diretores = tree.diretores || [];
    const managerOnly = tree.gerentes || null;
    if (!ownerId) {
      if (managerOnly) {
        return {
          level: "gerente",
          subject: { id: managerOnly[0]?.id, name: managerOnly[0]?.name, role: "manager" },
          children: (managerOnly[0]?.vendedores || []).map((s) => ({ ...s, level: "vendedor" })),
          parents: [],
        };
      }
      return {
        level: "empresa", subject: null,
        children: diretores.map((d) => ({
          id: d.id, name: d.name, role: "director", level: "diretor",
          count_managers: (d.gerentes || []).filter((g) => g.id).length,
          count_sellers: (d.gerentes || []).reduce((s, g) => s + (g.vendedores || []).length, 0),
        })),
        parents: [],
      };
    }
    for (const d of diretores) {
      if (d.id === ownerId) return {
        level: "diretor", subject: { id: d.id, name: d.name, role: "director" },
        children: (d.gerentes || []).filter((g) => g.id).map((g) => ({ id: g.id, name: g.name, role: "manager", level: "gerente", count_sellers: (g.vendedores || []).length })),
        parents: [{ level: "empresa", label: "Empresa" }],
      };
      for (const g of d.gerentes || []) {
        if (g.id === ownerId) return {
          level: "gerente", subject: { id: g.id, name: g.name, role: "manager" },
          children: (g.vendedores || []).map((s) => ({ ...s, level: "vendedor" })),
          parents: [{ level: "empresa", label: "Empresa" }, { level: "diretor", label: d.name, id: d.id }],
        };
        for (const s of g.vendedores || []) if (s.id === ownerId) return {
          level: "vendedor", subject: { id: s.id, name: s.name, role: "seller" },
          children: [],
          parents: [{ level: "empresa", label: "Empresa" }, { level: "diretor", label: d.name, id: d.id }, { level: "gerente", label: g.name, id: g.id }],
        };
      }
    }
    return { level: "empresa", subject: null, children: [], parents: [] };
  }, [tree, ownerId]);
}

/** Cartão de KPI clicável usado nos blocos de conquista. */
function KpiConquista({ icon: Icon, label, value, sub, tone = "navy", onClick, testId, help }) {
  const Comp = onClick ? "button" : "div";
  const toneCls = tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : tone === "bad" ? "text-bad" : tone === "brand" ? "text-brand" : "text-navy";
  return (
    <Comp onClick={onClick} data-testid={testId}
      className={`text-left bg-card rounded-2xl border border-black/5 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)] ${onClick ? "hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgb(0,0,0,0.07)] transition-all" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
          {label}
          {help && (
            <HelpTip label={help}>
              <span onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-muted-foreground hover:text-navy" data-testid={testId ? `${testId}-info` : "kpi-info"} aria-label="O que significa este número?">
                <Info className="w-3.5 h-3.5" />
              </span>
            </HelpTip>
          )}
        </span>
        <Icon className={`w-4 h-4 ${toneCls}`} />
      </div>
      <div className={`text-2xl font-bold mt-2 ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Comp>
  );
}

function ChildCard({ node, kpi, onClick }) {
  return (
    <button onClick={onClick} data-testid={`card-${node.level}-${node.id}`} className="text-left bg-card rounded-2xl border border-black/5 p-4 hover:border-brand/40 hover:shadow-[0_10px_28px_rgb(0,138,176,0.10)] transition-all">
      <div className="flex items-center gap-3">
        <Avatar name={node.name} size={36} />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{node.level === "diretor" ? "Diretor" : node.level === "gerente" ? "Gerente" : "Vendedor"}</div>
          <div className="font-semibold text-navy truncate">{node.name}</div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
      </div>
      {kpi ? (
        <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
          <div className="rounded-lg bg-slate-50 px-2 py-1.5">
            <div className="uppercase tracking-wider text-muted-foreground text-[10px]">Prospects</div>
            <div className="font-semibold text-navy">{num(kpi.prospects_novos?.count || 0)}</div>
          </div>
          <div className="rounded-lg bg-slate-50 px-2 py-1.5">
            <div className="uppercase tracking-wider text-muted-foreground text-[10px]">Pipeline</div>
            <div className="font-semibold text-navy">{num(kpi.pipeline_total?.count || 0)}</div>
          </div>
          <div className="rounded-lg bg-slate-50 px-2 py-1.5">
            <div className="uppercase tracking-wider text-muted-foreground text-[10px]">Parados 7d+</div>
            <div className={`font-semibold ${(kpi.parados_7d?.count || 0) > 0 ? "text-warn" : "text-navy"}`}>{num(kpi.parados_7d?.count || 0)}</div>
          </div>
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-muted-foreground">
          {node.count_managers != null ? `${node.count_managers} gerentes · ` : ""}
          {node.count_sellers != null ? `${node.count_sellers} vendedores` : ""}
        </div>
      )}
    </button>
  );
}

function PipelinePorEtapa({ data }) {
  const total = data.pipeline_total?.count || 0;
  if (!total) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Sem oportunidades ativas no funil.</p>;
  }
  const max = Math.max(...(data.pipeline_por_etapa || []).map((s) => s.count));
  return (
    <div className="space-y-2" data-testid="pipeline-por-etapa">
      {(data.pipeline_por_etapa || []).map((s) => (
        <div key={s.stage} className="flex items-center gap-3 text-sm">
          <span className="w-28 shrink-0 text-navy">{s.stage}</span>
          <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
            <div className="h-full transition-all" style={{ width: max ? `${(s.count / max) * 100}%` : "0%", backgroundColor: corEtapa(s.stage) }} />
          </div>
          <span className="w-10 text-right text-navy font-semibold">{s.count}</span>
          <span className="w-24 text-right text-muted-foreground">{brl(s.valor)}</span>
        </div>
      ))}
    </div>
  );
}

function SemDadosBox({ titulo, motivo, testId }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-600 mb-1"><Info className="w-4 h-4" /><span className="text-sm font-semibold">{titulo}</span></div>
      <p className="text-xs text-slate-500 leading-relaxed">Sem dados. {motivo}</p>
    </div>
  );
}
SemDadosBox._unused = true;


export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const ownerId = sp.get("owner_id") || "";
  const gestor = user?.role && ["admin", "director", "manager", "diretor_geral"].includes(user.role);

  const conquistaUrl = ownerId ? `/painel/conquista?owner_id=${ownerId}` : "/painel/conquista";
  const dashUrl = ownerId ? `/dashboard?owner_id=${ownerId}` : "/dashboard";
  const { data: conquista, loading: lc, error: ec, reload: rc } = useFetch(conquistaUrl);
  const { data: dash, loading: ld, error: ed, reload: rd } = useFetch(dashUrl);
  const { data: atencao } = useFetch(ownerId ? `/atencao?owner_id=${ownerId}` : "/atencao");

  const [tree, setTree] = useState(null);
  useEffect(() => {
    if (!gestor) return;
    api.get("/hierarquia").then((r) => setTree(r.data)).catch(() => {});
  }, [gestor]);

  const drill = useDrillChildren(tree, ownerId);

  // LOTE UX-1 B4 — card do vendedor exibe duas linhas discretas de "quanto falta"
  // + ticket médio. Só busca o painel quando o sujeito é vendedor (seller):
  // card de gerente/diretor/equipe NÃO muda. Sem recálculo — os números vêm
  // IGUAIS aos da tela Faturamento (mesmo endpoint).
  const vendedorSujeito = (dash?.subject?.role || user?.role) === "seller";
  const painelUrl = vendedorSujeito
    ? (ownerId ? `/sankhya/faturamento/painel?periodo=mes_atual&owner_id=${ownerId}` : "/sankhya/faturamento/painel?periodo=mes_atual")
    : null;
  const { data: painelMes } = useFetch(painelUrl);
  // "Home do próprio gerente" (self-view) vs "perfil de gerente aberto por
  // outra pessoa (diretor/diretor geral)". No self-view não faz sentido a
  // trilha e o título deve ser saudação, como nas homes de Vendedor / DG.
  const isSelfManagerHome =
    drill.subject?.role === "manager" && drill.subject?.id === user?.id;
  const isOtherManagerProfile =
    drill.subject?.role === "manager" && drill.subject?.id !== user?.id;

  // Resumo do dia + Meta batida — só na Home do próprio usuário (sem drill-down).
  // Dispara IMEDIATAMENTE ao montar, em paralelo com o /dashboard, para não
  // depender do carregamento completo das listas. Ver
  // CORRECAO_EMERGENT_MASCOTE_POSICAO.md — o painel precisa aparecer em ~2 s.
  const [resumoData, setResumoData] = useState(null);
  const [resumoAberto, setResumoAberto] = useState(false);
  const [metaBatidaAberta, setMetaBatidaAberta] = useState(false);
  const [registrarOpen, setRegistrarOpen] = useState(false);
  const [carteira, setCarteira] = useState([]);
  useEffect(() => {
    if (!registrarOpen || carteira.length) return;
    api.get("/clientes").then((r) => setCarteira(r.data || [])).catch(() => {});
  }, [registrarOpen, carteira.length]);
  useEffect(() => {
    if (ownerId) return undefined;
    let cancelled = false;
    api.get("/resumo-dia").then((r) => {
      if (cancelled) return;
      const d = r.data || {};
      setResumoData(d);
      if (d.mostrar_meta_batida) setMetaBatidaAberta(true);
      else if (d.mostrar) setResumoAberto(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [ownerId]);
  const [childKpis, setChildKpis] = useState({});
  const [atencaoAberto, setAtencaoAberto] = useState(() => {
    try { return localStorage.getItem("mpires_atencao_open") !== "0"; } catch { return true; }
  });
  useEffect(() => {
    setChildKpis({});
    if (!drill.children.length) return;
    let cancelled = false;
    Promise.all(drill.children.map((c) => api.get(`/painel/conquista?owner_id=${c.id}`).then((r) => [c.id, r.data]).catch(() => [c.id, null])))
      .then((pairs) => { if (!cancelled) setChildKpis(Object.fromEntries(pairs.filter(([, v]) => v))); });
    return () => { cancelled = true; };
  }, [drill.level, drill.children.map((c) => c.id).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const goOwner = (id) => {
    const params = new URLSearchParams(sp);
    if (id) params.set("owner_id", id); else params.delete("owner_id");
    setSp(params);
  };
  const voltar = () => {
    if (drill.parents.length) { const prev = drill.parents[drill.parents.length - 1]; goOwner(prev.id || ""); }
    else navigate(-1);
  };

  if (user?.role === "diretor_geral" && !ownerId) return <Navigate to="/operacao" replace />;
  if (lc || ld) return <DashboardSkeleton />;
  if (ec || ed) return <ErrorState onRetry={() => { rc(); rd(); }} />;

  const progress = Math.min(100, dash.progress || 0);
  const ring = [{ name: "atingido", value: progress }, { name: "resto", value: 100 - progress }];
  const showDrill = gestor && (drill.parents.length > 0 || drill.children.length > 0);
  const perdidosMotivos = conquista.perdidos?.por_motivo || [];
  const isSeller = user?.role === "seller";
  const atencaoOpen = atencaoAberto;
  const toggleAtencao = () => setAtencaoAberto((v) => { const nv = !v; try { localStorage.setItem("mpires_atencao_open", nv ? "1" : "0"); } catch { /* ignore */ } return nv; });

  return (
    <div className="pb-24 lg:pb-0">
      {/* Lote 4 item 1 — quadro "Precisa de atenção agora" removido do Início
          para todos os perfis. A tela /atencao, o sino e o banner "Tudo em
          dia!" continuam funcionando com o mesmo fetch `atencao`. */}

      {showDrill && !isSelfManagerHome ? (
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3 flex-wrap" data-testid="painel-crumbs">
          <button onClick={() => goOwner("")} className="flex items-center gap-1 hover:text-navy" data-testid="crumb-empresa"><Home className="w-3.5 h-3.5" /> Empresa</button>
          {drill.parents.slice(1).map((p) => (
            <span key={`${p.level}-${p.id}`} className="flex items-center gap-1">
              <ChevronRight className="w-3.5 h-3.5" />
              <button onClick={() => goOwner(p.id || "")} className="hover:text-navy" data-testid={`crumb-${p.level}`}>{p.label}</button>
            </span>
          ))}
          {drill.subject ? <span className="flex items-center gap-1"><ChevronRight className="w-3.5 h-3.5" /><span className="text-navy font-medium" data-testid="crumb-atual">{drill.subject.name}</span></span> : null}
        </div>
      ) : null}

      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-navy">
            {(drill.subject && !isSelfManagerHome) ? drill.subject.name : `Olá, ${user?.name?.split(" ")[0]}`}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSelfManagerHome
              ? `Painel do gerente · ${conquista.periodo.label}`
              : isOtherManagerProfile
                ? `Desempenho do gerente · ${conquista.periodo.label}`
                : `Conquista · ${conquista.scope_label} · ${conquista.periodo.label}`}
          </p>
        </div>
        {showDrill && drill.parents.length > 0 ? (
          <button onClick={voltar} data-testid="painel-voltar" className="flex items-center gap-1.5 h-10 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50"><ArrowLeft className="w-4 h-4" /> Voltar</button>
        ) : null}
      </div>

      <ReminderBanner ownerId={ownerId} />
      {/* UX-15 / UX-17 Lote 1 → LOTE UX-2B item 5: só vendedor vê o botão
          "Registrar atividade" no topo da Início. Para gestores (gerente,
          diretor, diretor geral e admin) a entrada principal passa a ser
          pela oportunidade e pelo "+" da barra inferior. */}
      {user?.role === "seller" && (
        <div className="mb-6 hidden md:flex justify-end">
          <button
            data-testid="home-registrar-atividade"
            onClick={() => setRegistrarOpen(true)}
            className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors"
          >
            <Plus className="w-4 h-4" /> Registrar atividade
          </button>
        </div>
      )}
      {!ownerId && <CotaPendenteBanner />}
      {/* Fase 2 · Set/2026: os cards Sankhya foram removidos daqui. O único
          faturamento na Início vem do quadro azul de meta abaixo (para vendedor/
          gerente/diretor) ou do card específico do DG. Admin não vê nada. */}
      {!ownerId && user?.role === "diretor_geral" && <FaturamentoSankhyaCards />}

      {/* Card "Cota real" (anel + faturado/cota do mês) — Gerente, Diretor e
          Diretor Geral. Vendedor e Admin NÃO veem. */}
      {!ownerId && ["manager", "director", "diretor_geral"].includes(user?.role) && (
        <div className="mb-6"><CotaRealCard faturadoMes={dash.revenue} /></div>
      )}

      {/* META INDIVIDUAL — Vendedor / Gerente / Diretor.
          Admin (perfil técnico) e Diretor Geral não veem o quadro de meta. */}
      {!["admin", "diretor_geral"].includes(dash.subject?.role || user?.role) && (
      <Dica id="home-quadro-meta" texto={DICAS.homeQuadroMeta} className="mb-6">
      <div className="rounded-2xl overflow-hidden shadow-[0_6px_24px_rgb(0,138,176,0.15)] bg-gradient-to-r from-navy to-brand text-white" data-testid="bloco-meta-topo">
        <div className="p-5 md:p-6">
          <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 sm:justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 opacity-80" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80">Meta · {dash.quota_mode}</span>
            </div>
            <span className="text-[10px] uppercase tracking-wider opacity-70 basis-full sm:basis-auto sm:ml-auto" data-testid="rot-sankhya">
              Faturamento Sankhya{dash.atualizado_em_sankhya ? ` · atualizado às ${new Date(dash.atualizado_em_sankhya).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={ring} dataKey="value" innerRadius={32} outerRadius={44} startAngle={90} endAngle={-270} stroke="none">
                    <Cell fill="#FFFFFF" /><Cell fill="rgba(255,255,255,0.18)" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-extrabold">{pct(dash.achievement)}</span>
                <span className="text-[9px] uppercase tracking-wider opacity-70">atingido</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              {dash.sem_vinculo_sankhya ? (
                <div className="text-lg md:text-xl font-semibold leading-tight" data-testid="sem-vinculo-sankhya">
                  Sem vínculo com o Sankhya
                  <div className="text-xs opacity-80 mt-1 font-normal">Peça ao Administrador para vincular seu CODVEND em Ajustes → Sankhya.</div>
                </div>
              ) : (
                <>
                  <div className="text-2xl md:text-3xl font-bold leading-tight break-words">{brl(dash.revenue)}</div>
                  <div className="text-xs opacity-80 mt-0.5">de {brl(dash.goal)} de meta</div>
                  <div className="h-2 bg-white/20 rounded-full mt-2.5 overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-700 shadow-[0_0_12px_rgba(255,255,255,0.6)]" style={{ width: `${progress}%` }} />
                  </div>
                  {dash.vendas_diretas_sankhya != null && dash.vendas_diretas_sankhya > 0 && (
                    <Dica id="home-vendas-diretas" texto={DICAS.homeVendasDiretas} className="mt-2">
                    <div className="text-[11px] rounded-lg bg-white/15 px-2.5 py-1.5" data-testid="vendas-diretas">
                      <b>Minhas vendas diretas:</b> {brl(dash.vendas_diretas_sankhya)} · incluídas no total {dash.quota_mode === "Equipe" ? "da equipe" : "da diretoria"}
                    </div>
                    </Dica>
                  )}
                </>
              )}
              {/* LOTE UX-1 B4 — Linhas discretas de "quanto falta" + ticket médio.
                  Aparece SOMENTE para vendedor (card do gerente/diretor não muda).
                  Valores vêm do mesmo endpoint da tela Faturamento (sem recálculo). */}
              {vendedorSujeito && dash.sem_vinculo_sankhya && (
                <div className="mt-2 text-[11px] opacity-80 leading-snug" data-testid="meta-sem-dados">
                  Sem dados do Sankhya para calcular o ritmo.
                </div>
              )}
              {vendedorSujeito && !dash.sem_vinculo_sankhya && painelMes?.totais && (() => {
                const t = painelMes.totais || {};
                const u = painelMes.dias_uteis || {};
                const temMeta = !!painelMes.tem_meta;
                // falta_meta vem como (meta - liquido): negativo = acima da meta.
                const metaBatida = !!t.meta_batida;
                const nfmt = (n) => `R$ ${new Intl.NumberFormat("pt-BR").format(Math.round(Number(n) || 0))}`;
                const pctfmt = (p) => `${Number(p || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%`;
                const acima = Math.max(0, -(Number(t.falta_meta) || 0));
                const linha1 = metaBatida
                  ? (acima > 0
                      ? `Meta batida! ${nfmt(acima)} acima da meta.`
                      : "Meta batida!")
                  : (temMeta
                      ? `Faltam ${nfmt(t.falta_meta)} · ${nfmt(t.falta_meta_por_dia_util)}/dia útil (${u.restantes ?? 0} dias)`
                      : null);
                const linha2Nums = `Ticket médio ${nfmt(t.ticket_medio)} (${t.notas_venda || 0} notas)`;
                const linha2 = temMeta
                  ? `${linha2Nums} · No ritmo atual: ${pctfmt(t.projecao_pct_meta)} da meta`
                  : linha2Nums;
                return (
                  <div className="mt-2 space-y-0.5 text-[11px] opacity-80 leading-snug">
                    {linha1 && <div className="break-words" data-testid="meta-falta">{linha1}</div>}
                    <div className="break-words" data-testid="meta-ticket">{linha2}</div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      </Dica>
      )}

      {atencao && atencao.count === 0 && !ownerId && (
        <div className="mb-6 rounded-xl bg-ok-soft border border-ok/20 p-4 flex items-center gap-3" data-testid="tudo-em-dia">
          <CheckCircle2 className="w-5 h-5 text-ok shrink-0" />
          <div className="flex-1 text-sm"><span className="font-semibold text-ok">Tudo em dia!</span><span className="text-ok/80"> — nenhuma pendência crítica no seu funil.</span></div>
        </div>
      )}

      {/* INDICADORES DE CONQUISTA (Mensagem 3) — foco principal */}
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Indicadores de conquista</div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="conquista-kpis">
        <KpiConquista icon={UserPlus} label="Prospects novos" value={num(conquista.prospects_novos.count)} tone="brand" onClick={() => navigate("/clientes")} testId="kpi-prospects-novos" help="Clientes com status Prospect criados no período (insights.painel_conquista)." />
        <KpiConquista icon={KanbanSquare} label="Pipeline aberto" value={num(conquista.pipeline_total.count)} sub={brl(conquista.pipeline_total.valor)} onClick={() => navigate("/pipeline")} testId="kpi-pipeline-aberto" help="Soma do valor de todas as oportunidades abertas (insights.painel_conquista → pipeline_total)." />
        <KpiConquista icon={PauseCircle} label="Parados 7d+" value={num(conquista.parados_7d.count)} sub={brl(conquista.parados_7d.valor)} tone={conquista.parados_7d.count > 0 ? "warn" : "navy"} onClick={() => navigate("/foco/paradas")} testId="kpi-parados-7d" help="Oportunidades abertas sem movimentação há 7 dias ou mais (insights.painel_conquista → parados_7d)." />
        <KpiConquista icon={Trophy} label="Ganhos no mês" value={num(conquista.ganhos.count)} sub={brl(conquista.ganhos.valor)} tone="ok" testId="kpi-ganhos" help="Soma do valor fechado das oportunidades ganhas no mês (insights._valor_efetivo → painel_conquista.ganhos)." />
        <KpiConquista icon={XCircle} label="Perdidos no mês" value={num(conquista.perdidos.count)} sub={brl(conquista.perdidos.valor)} tone="bad" testId="kpi-perdidos" help="Soma do valor das oportunidades perdidas no mês (insights.painel_conquista → perdidos)." />
        <KpiConquista icon={Percent} label="Taxa de conversão" value={conquista.taxa_conversao == null ? "—" : pct(conquista.taxa_conversao, 1)} tone={(conquista.taxa_conversao || 0) >= 30 ? "ok" : "navy"} testId="kpi-conversao" help="Clientes ganhos ÷ (clientes ganhos + perdidos) no mês (reports._clientes_ganhos_perdidos)." />
      </div>

      {/* Drill-down: cards por nível — com indicadores de conquista */}
      {gestor && drill.children.length > 0 ? (
        <div className="mb-6" data-testid="painel-drilldown">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {drill.level === "empresa" ? "Diretores" : drill.level === "diretor" ? "Gerentes" : "Vendedores"}
              <span className="text-slate-400 font-normal ml-2">({drill.children.length})</span>
            </span>
            <button onClick={() => navigate(`/relatorio-gerencial${ownerId ? `?${drill.level === "diretor" ? "diretor_id" : drill.level === "gerente" ? "gerente_id" : "vendedor_id"}=${ownerId}` : ""}`)} className="text-xs font-medium text-brand hover:text-brand-hover" data-testid="ver-relatorio-nivel">Ver no relatório →</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {drill.children.map((c) => (
              <ChildCard key={c.id} node={c} kpi={childKpis[c.id]} onClick={() => goOwner(c.id)} />
            ))}
          </div>
        </div>
      ) : null}

      {/* Pipeline por etapa + Perdas por motivo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pipeline por etapa</span>
            <button onClick={() => navigate("/pipeline")} data-testid="dash-ver-funil" className="inline-flex items-center gap-1 min-h-[44px] px-3 -mr-2 text-xs font-medium text-brand hover:text-brand-hover">Ver funil →</button>
          </div>
          <PipelinePorEtapa data={conquista} />
        </div>
        <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="perdas-por-motivo">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Perdas por motivo</div>
          {perdidosMotivos.length ? (
            <div className="flex flex-wrap gap-2">
              {perdidosMotivos.map((m) => (
                <span key={m.motivo} className="text-xs bg-slate-100 rounded-full px-3 py-1.5 text-slate-700" data-testid={`motivo-${m.motivo}`}>
                  {m.motivo} <span className="font-semibold text-navy">· {m.count}</span> <span className="text-slate-500">· {brl(m.valor)}</span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-6 text-center">Sem perdas no período.</p>
          )}
        </div>
      </div>

      {/* Ampliação de mix (ex "Cross-selling") + Distribuição por segmento */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="cross-selling-bloco">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Ampliação de mix aberta</div>
          <div className="flex items-center gap-4">
            <div>
              <div className="text-2xl font-bold text-navy">{num(conquista.cross_selling.abertas_count)}</div>
              <div className="text-xs text-muted-foreground">{brl(conquista.cross_selling.abertas_valor)} em aberto</div>
            </div>
            <div className="border-l border-black/5 pl-4">
              <div className="text-sm text-ok font-semibold">{num(conquista.cross_selling.ganhos_count)} ganhos</div>
              <div className="text-xs text-muted-foreground">{brl(conquista.cross_selling.ganhos_valor)}</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="segmento-bloco">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Distribuição por segmento</div>
          {conquista.distribuicao_segmento.items.length ? (
            <div className="flex flex-wrap gap-2">
              {conquista.distribuicao_segmento.items.map((s) => (
                <span key={s.segmento} className="text-xs bg-slate-100 rounded-full px-3 py-1.5 text-slate-700" data-testid={`segmento-${s.segmento}`}>
                  {s.segmento} <span className="font-semibold text-navy">· {s.count}</span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Sem clientes no escopo.</p>
          )}
        </div>
      </div>

      {/* Meta / Faturado — bloco secundário */}
      <details className="mb-6 group hidden" data-testid="bloco-meta-secundario">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none list-none flex items-center gap-2 mb-2 hover:text-navy">
          <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
          Meta · {dash.quota_mode} <span className="text-slate-400 font-normal normal-case">(faturamento oficial)</span>
        </summary>
      </details>

      {/* UX-15 / UX-17 Lote 1: cartão "Ações principais" removido do rodapé —
          o botão "Registrar atividade" subiu para o topo da Início. */}

      {/* PEDIDO_EMERGENT_2A_UMA_PORTA — FAB do vendedor abre a tela Prospectar
          (uma porta única com Cadastro completo / Foto na rua / Buscar no mapa). */}
      {isSeller && (
        <button
          onClick={() => navigate("/captar")}
          data-testid="fab-novo-prospect"
          className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 h-14 pl-5 pr-6 rounded-full bg-brand text-white text-sm font-semibold shadow-[0_10px_28px_rgb(0,138,176,0.35)] hover:bg-brand-hover hover:shadow-[0_14px_36px_rgb(0,138,176,0.45)] transition-all flex items-center gap-2 z-30"
        >
          <Plus className="w-5 h-5" />
          Novo prospect
        </button>
      )}
      {metaBatidaAberta && resumoData && (
        <MetaBatida data={resumoData} onClose={() => setMetaBatidaAberta(false)} />
      )}
      {resumoAberto && resumoData && !metaBatidaAberta && (
        <ResumoDoDia data={resumoData} onClose={() => setResumoAberto(false)} />
      )}
      <RegistrarAtividade
        open={registrarOpen}
        onOpenChange={setRegistrarOpen}
        carteira={carteira}
      />
    </div>
  );
}
